import React from "react";
import { Stack } from "components/layout/reusableLayouts";
import { useComments, CommentActions } from "hooks/useComments";
import CommentList from "./CommentList";
import CommentComposer from "./CommentComposer";

interface CommentsProps {
  /**
   * Id of the entity these comments belong to. Pass an empty string when the
   * parent entity doesn't exist yet (e.g. a new payment being created): the
   * component then renders in "draft" mode, where the existing-comments list and the
   * Post button are hidden, and submission must be triggered by the parent
   * via `commentActions.submit(newParentId)` once the entity is created.
   */
  parentId: string;
  parentType: string;
  rows?: number;
  placeholder?: string;
  /**
   * Optional externally-owned `useComments` instance. Pass this when the
   * parent component needs to control submission timing, e.g. submit a
   * draft comment after its parent entity (payment, invoice) is saved.
   * When omitted, this component owns its own `useComments` internally.
   */
  commentActions?: CommentActions;
  openProfileOnMention?: boolean;
}

/** The comment list with its composer beneath, for a drawer body or accordion. */
const Comments: React.FC<CommentsProps> = ({
  parentId,
  parentType,
  rows,
  placeholder,
  commentActions: externalActions,
  openProfileOnMention,
}) => {
  // Hooks cannot be conditional, so the internal one always runs. Feeding it
  // an empty parentId when the caller supplies its own actions puts it in
  // draft mode, where `useComments` skips the query, rather than firing a
  // second network-only GET_COMMENTS for the same parent.
  const internalActions = useComments(
    externalActions ? "" : parentId,
    parentType,
  );
  const actions = externalActions ?? internalActions;

  return (
    <Stack gap="var(--space-5)">
      <CommentList
        actions={actions}
        openProfileOnMention={openProfileOnMention}
      />
      <CommentComposer
        actions={actions}
        rows={rows}
        placeholder={placeholder}
      />
    </Stack>
  );
};

export default Comments;
